import { type BackgroundColor } from '@ironhack/design-system2/utils';
import { setDay } from 'date-fns';
import {
  always,
  anyPass,
  drop,
  find,
  findLast,
  flip,
  ifElse,
  invert,
  isEmpty,
  isNil,
  map,
  mapObjIndexed,
  pipe,
  prop,
  reject,
  replace,
  split,
  toLower,
  toUpper,
  when,
} from 'ramda';
import { type StructuredTextGraphQlResponse } from 'react-datocms';

import { type DatoTimeZone } from '@/lib/datocms/dates';
import { type DatoLocale } from '@/lib/datocms/types';
import {
  type RemoteTimezone,
  getCohortTime,
  getTimezoneAbbreviation,
} from '@/lib/utils/dates';
import {
  type Language,
  getLanguageFromLocale,
  getLanguageName,
} from '@/lib/utils/language';
import { toStandardLocale } from '@/lib/utils/locale';
import {
  type AvailableCourseTrack,
  type Campus,
  type Region,
  type RegionCountry,
  type WebCohort,
} from '@/types';

export const toCamelCase = pipe(
  toLower,
  replace(/[\s-]\S/gu, toUpper),
  replace(/-/gu, '')
);

export const toTitleCase = pipe(
  toLower,
  replace(/(^|[\s-])\S/gu, toUpper),
  replace(/-/gu, ' ')
);

export const toUpperPascalCase = pipe(
  toLower,
  replace(/(^|[\s-])\S/gu, toUpper),
  replace(/-/gu, '')
);

export const regionToTimezoneMap: Record<Region, RemoteTimezone | ''> = {
  de: 'Europe/Madrid',
  es: 'Europe/Madrid',
  fr: 'Europe/Madrid',
  nl: 'Europe/Madrid',
  pt: 'Europe/Madrid',
  uk: 'Europe/Madrid',
  us: 'Europe/Madrid',
  ww: '',
};

export const countryToRegion: Record<RegionCountry, Region> = {
  de: 'de',
  es: 'es',
  fr: 'fr',
  gb: 'uk',
  nl: 'nl',
  pt: 'pt',
  us: 'us',
};

export const regionToCountries = invert(countryToRegion) as Record<
  Region,
  RegionCountry[]
>;

export const regionToMainCountry = (region: Region): RegionCountry =>
  region === 'ww' ? 'us' : region === 'uk' ? 'gb' : region;

// The first campus sets the price for remote cohorts in the region
export const regionToCampusMap: Record<Region, Campus[]> = {
  de: ['ber'],
  es: ['mad', 'bcn'],
  fr: ['par'],
  nl: ['ams'],
  pt: ['lis'],
  uk: ['lon', 'rmt'],
  us: ['rmt'],
  ww: ['rmt'],
};

/* eslint-disable sort-keys */
export const countryToTimezone: Record<string, RemoteTimezone> = {
  // Europe
  BE: 'Europe/Madrid',
  BG: 'Europe/Madrid',
  CZ: 'Europe/Madrid',
  DK: 'Europe/Madrid',
  DE: 'Europe/Madrid',
  EE: 'Europe/Madrid',
  IE: 'Europe/Madrid',
  EL: 'Europe/Madrid',
  ES: 'Europe/Madrid',
  FR: 'Europe/Madrid',
  HR: 'Europe/Madrid',
  IT: 'Europe/Madrid',
  CY: 'Europe/Madrid',
  LV: 'Europe/Madrid',
  LT: 'Europe/Madrid',
  LU: 'Europe/Madrid',
  HU: 'Europe/Madrid',
  MT: 'Europe/Madrid',
  NL: 'Europe/Madrid',
  AT: 'Europe/Madrid',
  PL: 'Europe/Madrid',
  PT: 'Europe/Madrid',
  RO: 'Europe/Madrid',
  SI: 'Europe/Madrid',
  SK: 'Europe/Madrid',
  FI: 'Europe/Madrid',
  SE: 'Europe/Madrid',
  IS: 'Europe/Madrid',
  LI: 'Europe/Madrid',
  NO: 'Europe/Madrid',
  CH: 'Europe/Madrid',
  ME: 'Europe/Madrid',
  MK: 'Europe/Madrid',
  AL: 'Europe/Madrid',
  RS: 'Europe/Madrid',
  TR: 'Europe/Madrid',
  BA: 'Europe/Madrid',
  XK: 'Europe/Madrid',
  UK: 'Europe/Madrid',
  GB: 'Europe/Madrid',
};
/* eslint-enable sort-keys */

export const commonKeys: Record<Language, Record<string, string>> = {
  de: {
    ac: 'KI Consulting',
    ai: 'Künstliche Intelligenz',
    ams: 'Amsterdam',
    bcn: 'Barcelona',
    ber: 'Berlin',
    ce: 'Cloud engineering',
    cy: 'Cybersecurity',
    da: 'Data analytics',
    de: 'Data engineering',
    dv: 'DevOps & Cloud Computing',
    ft: 'Vollzeit',
    lis: 'Lissabon',
    lon: 'London',
    mad: 'Madrid',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'Im Campus',
    par: 'Paris',
    pm: 'KI Product Manager',
    pt: 'Teilzeit',
    region_de: 'Deutschland',
    region_es: 'Spanien',
    region_fr: 'Frankreich',
    region_nl: 'Niederlande',
    region_pt: 'Portugal',
    region_uk: 'Großbritannien',
    region_us: 'Vereinigte Staaten',
    region_ww: 'Welt',
    remote: 'Remote/Online',
    rmt: 'Remote',
    ux: 'UX/UI Design',
    wd: 'Web development',
    weeks: 'Wochen',
  },
  en: {
    ac: 'AI Consulting',
    ai: 'Artificial Intelligence',
    ams: 'Amsterdam',
    bcn: 'Barcelona',
    ber: 'Berlin',
    ce: 'Cloud engineering',
    cy: 'Cybersecurity',
    da: 'Data analytics',
    de: 'Data engineering',
    dv: 'DevOps & Cloud Computing',
    ft: 'Full time',
    lis: 'Lisbon',
    lon: 'London',
    mad: 'Madrid',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'In Campus',
    par: 'Paris',
    pm: 'AI Product Management',
    pt: 'Part time',
    region_de: 'Germany',
    region_es: 'Spain',
    region_fr: 'France',
    region_nl: 'Netherlands',
    region_pt: 'Portugal',
    region_uk: 'United Kingdom',
    region_us: 'United States',
    region_ww: 'Worldwide',
    remote: 'Remote/Online',
    rmt: 'Remote',
    ux: 'UX/UI Design',
    wd: 'Web development',
    weeks: 'Weeks',
  },
  es: {
    ac: 'Consultor IA',
    ai: 'Inteligencia Artificial',
    ams: 'Amsterdam',
    bcn: 'Barcelona',
    ber: 'Berlín',
    ce: 'Cloud engineering',
    cy: 'Ciberseguridad',
    da: 'Análisis de Datos',
    de: 'Data engineering',
    dv: 'DevOps & Cloud Computing',
    ft: 'Tiempo completo',
    lis: 'Lisboa',
    lon: 'Londres',
    mad: 'Madrid',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'En campus',
    par: 'Paris',
    pm: 'Product Management IA',
    pt: 'Tiempo parcial',
    region_de: 'Alemania',
    region_es: 'España',
    region_fr: 'Francia',
    region_nl: 'Países Bajos',
    region_pt: 'Portugal',
    region_uk: 'Reino Unido',
    region_us: 'Estados Unidos',
    region_ww: 'Resto Del Mundo',
    remote: 'Remoto/Online',
    rmt: 'Remoto',
    ux: 'Diseño UX/UI',
    wd: 'Desarrollo Web',
    weeks: 'Semanas',
  },
  fr: {
    ac: 'Consultant IA',
    ai: 'Intelligence Artificielle',
    ams: 'Amsterdam',
    bcn: 'Barcelone',
    ber: 'Berlin',
    ce: 'Cloud engineering',
    cy: 'Cybersécurité',
    da: 'Data analyse',
    de: 'Data engineering',
    dv: 'DevOps & Cloud Computing',
    ft: 'Plein temps',
    lis: 'Lisbonne',
    lon: 'Londres',
    mad: 'Madrid',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'Sur le campus',
    par: 'Paris',
    pm: 'Product Manager IA',
    pt: 'Temps partiel',
    region_de: 'Allemagne',
    region_es: 'Espagne',
    region_fr: 'France',
    region_nl: 'Pays-Bas',
    region_pt: 'Portugal',
    region_uk: 'Royaume-Uni',
    region_us: 'États-Unis',
    region_ww: 'Monde',
    remote: 'À distance/En ligne',
    rmt: 'Remote',
    ux: 'UX/UI Design',
    wd: 'Développement web',
    weeks: 'Semaines',
  },
  nl: {
    ac: 'AI Consulting',
    ai: 'Kunstmatige Intelligentie',
    ams: 'Amsterdam',
    bcn: 'Barcelona',
    ber: 'Berlijn',
    ce: 'Cloud engineering',
    cy: 'Cybersecurity',
    da: 'Data analyse',
    de: 'Data engineering',
    dv: 'DevOps & Cloud Computing',
    ft: 'Full time',
    lis: 'Lissabon',
    lon: 'Londen',
    mad: 'Madrid',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'Op de campus',
    par: 'Parijs',
    pm: 'AI Product Management',
    pt: 'Part time',
    region_de: 'Duitsland',
    region_es: 'Spanje',
    region_fr: 'Frankrijk',
    region_nl: 'Nederland',
    region_pt: 'Portugal',
    region_uk: 'Verenigd Koninkrijk',
    region_us: 'Verenigde Staten',
    region_ww: 'Wereld',
    remote: 'Remote/Online',
    rmt: 'Remote',
    ux: 'UX/UI Design',
    wd: 'Web development',
    weeks: 'Weken',
  },
  pt: {
    ac: 'Consultoria e Integração de IA ',
    ai: 'Inteligência Artificial',
    ams: 'Amsterdão',
    bcn: 'Barcelona',
    ber: 'Berlim',
    ce: 'Cloud engineering',
    cy: 'Cibersegurança',
    da: 'Análise de Dados',
    de: 'Engenharia de Dados',
    dv: 'DevOps & Cloud Computing',
    ft: 'Tempo inteiro',
    lis: 'Lisboa',
    lon: 'Londres',
    mad: 'Madri',
    mia: 'Miami',
    mk: 'Digital Marketing',
    ml: 'Data Science & Machine Learning',
    onCampus: 'No campus',
    par: 'Paris',
    pm: 'Product Manager IA',
    pt: 'Pós-laboral',
    region_de: 'Alemanha',
    region_es: 'Espanha',
    region_fr: 'França',
    region_nl: 'Holanda',
    region_pt: 'Portugal',
    region_uk: 'Reino Unido',
    region_us: 'Estados Unidos',
    region_ww: 'Mondial',
    remote: 'Remoto/Online',
    rmt: 'Remoto',
    ux: 'Design UX/UI',
    wd: 'Desenvolvimento Web',
    weeks: 'Semanas',
  },
};

export const cleanObject = reject(anyPass([isNil, isEmpty]));

export const regionCodesToEnglishNames: Record<Region, string> = {
  de: 'Germany',
  es: 'Spain',
  fr: 'France',
  nl: 'Netherlands',
  pt: 'Portugal',
  uk: 'United Kingdom',
  us: 'United States',
  ww: 'Worldwide',
};

export const formatCohortDate = (
  cohortDate: string,
  language: Language | DatoLocale
): string =>
  new Date(cohortDate).toLocaleDateString(
    toStandardLocale(language as DatoLocale),
    { day: 'numeric', month: 'short', timeZone: 'UTC' }
  );

const PRICE_FORMAT_OPTIONS = {
  style: 'currency',
  trailingZeroDisplay: 'stripIfInteger',
  useGrouping: true,
} as const;

export const getCohortCardIconList = (
  cohort: WebCohort,
  locale: DatoLocale,
  timeZone: Pick<DatoTimeZone, 'abbreviation' | 'name'> | undefined,
  options?: Partial<{ omitItems: string[]; showEndPeriod: boolean }>
): StructuredTextGraphQlResponse => {
  const { omitItems, showEndPeriod } = {
    omitItems: [],
    showEndPeriod: true,
    ...options,
  };
  const startDate = new Date(cohort.start_date);
  const startDateFormatted = formatCohortDate(cohort.start_date, locale);
  const localizedEndDate = cohort.localized?.end_date || cohort.end_date;
  const endDate = new Date(localizedEndDate);
  const endDateFormatted = formatCohortDate(localizedEndDate, locale);

  const weeksNumber = Math.round(
    Math.abs(endDate.getTime() - startDate.getTime()) /
      (1000 * 60 * 60 * 24 * 7) // One week in milliseconds
  );
  const language = getLanguageFromLocale(locale);
  const keys = commonKeys[language];
  const dates = `${startDateFormatted} - ${
    showEndPeriod ? endDateFormatted : ''
  }`;
  const format = `${keys[cohort.format]} - ${
    showEndPeriod ? `(${weeksNumber} ${keys.weeks})` : ''
  }`;
  const languageFormatted = getLanguageName(cohort.language, language);
  const location = `${
    keys[cohort.campus === 'rmt' ? 'remote' : 'onCampus']
  } - ${languageFormatted}`;

  const priceLocale = toStandardLocale(locale);
  const priceOptions = {
    currency: cohort.price.currency,
    ...PRICE_FORMAT_OPTIONS,
  };
  const price = cohort.price.amount.toLocaleString(priceLocale, priceOptions);
  const discountPrice = (
    cohort.price.amount - cohort.price.discount || 0
  ).toLocaleString(priceLocale, priceOptions);

  const timeZoneText = timeZone
    ? `${timeZone.name} (${timeZone.abbreviation})`
    : getTimezoneAbbreviation(cohort.timezone, language);

  return {
    blocks: [
      {
        __typename: 'ListWithIconsBlockRecord',
        id: '55723067',
        listItems: [
          {
            id: 'dates',
            text: dates,
            icon: [
              {
                __typename: 'IconRecord',
                name: 'calendar',
                color: '',
                placement: '',
              },
            ],
          },
          {
            id: 'format',
            text: format,
            icon: [
              {
                __typename: 'IconRecord',
                name: 'clock',
                color: '',
                placement: '',
              },
            ],
          },
          {
            id: 'location',
            text: location,
            icon: [
              {
                __typename: 'IconRecord',
                name: 'map-pin',
                color: '',
                placement: '',
              },
            ],
          },
          {
            id: 'timeZone',
            text: timeZoneText,
            icon: [
              {
                __typename: 'IconRecord',
                name: 'globe',
                color: '',
                placement: '',
              },
            ],
          },
          {
            id: 'price',
            text: discountPrice,
            icon: [
              {
                __typename: 'IconRecord',
                name: 'tag',
                color: '',
                placement: '',
              },
            ],
          },
        ].filter(({ id }) => !omitItems.includes(id)),
      },
    ],
    links: [],
    value: {
      schema: 'dast',
      document: {
        type: 'root',
        children: [
          {
            item: '55723067',
            type: 'block',
          },
          ...(price !== discountPrice && !omitItems.includes('price')
            ? [
                {
                  children: [
                    {
                      marks: ['strikethrough'],
                      type: 'span' as const,
                      value: price,
                    },
                  ],
                  type: 'paragraph' as const,
                },
              ]
            : []),
        ],
      },
    },
  };
};

export const getPrettyCohort = (
  cohort: WebCohort,
  language: Language,
  options?: Partial<{ useCohortTimezone: boolean }>
) => {
  const timezone = options?.useCohortTimezone
    ? cohort.timezone
    : cohort.campus === 'rmt'
    ? // eslint-disable-next-line new-cap -- It's a JS native lib, so it doesn't apply
      Intl.DateTimeFormat(language).resolvedOptions().timeZone
    : cohort.timezone;
  const showDatesInUserTimezone = cohort.campus === 'rmt';
  const formatTime = getCohortTime(
    cohort.timezone,
    timezone,
    cohort.start_date
  );

  const getCohortTimes = (
    isWeekend: boolean
  ): Exclude<WebCohort['timetable'][number], null> =>
    // @ts-expect-error: Can't figure out why pipe returns unknown
    pipe(
      // @ts-expect-error: Can't figure out how to make ifElse play nice
      ifElse(always(isWeekend), findLast(Boolean), find(Boolean)),
      mapObjIndexed((t: string) => t.padStart(5, '0')),
      when(always(showDatesInUserTimezone), map(formatTime))
    )(cohort.timetable);
  const cohortWeekDayTimes = getCohortTimes(false);
  const cohortWeekendTimes =
    cohort.format === 'ft'
      ? { start_time: '', end_time: '' }
      : getCohortTimes(true);

  const cohortStartDate = new Date(cohort.start_date);
  const dayFormatter = new Intl.DateTimeFormat([language], {
    weekday: 'short',
  });

  const [weekDays, weekendDays] = cohort.timetable.reduce<[string[], string[]]>(
    (acc, times, dayIndex) => {
      const dayName = dayFormatter.format(setDay(cohortStartDate, dayIndex));
      const isWeekend = dayIndex === 0 || dayIndex === 6;
      const addWeekDay =
        times &&
        !isWeekend &&
        (!cohort.timetable[dayIndex - 1] || dayIndex === 5);
      if (addWeekDay) {
        return [[...acc[0], dayName], acc[1]];
      }
      const addWeekendDay = times && isWeekend;
      if (addWeekendDay) {
        return [acc[0], [...acc[1], dayName]];
      }
      return acc;
    },
    [[], []]
  );

  const keys = commonKeys[language];

  const priceOptions = {
    currency: cohort.price.currency,
    ...PRICE_FORMAT_OPTIONS,
  };

  return {
    campus: keys[cohort.campus],
    discount: cohort.price.discount.toLocaleString(language, priceOptions),
    discountPrice: (
      cohort.price.amount - cohort.price.discount || 0
    ).toLocaleString(language, priceOptions),
    endDate: formatCohortDate(
      cohort.localized?.end_date || cohort.end_date,
      language
    ),
    externalIds: cohort.external_ids,
    format: keys[cohort.format],
    id: cohort.id,
    language: getLanguageName(cohort.language, language),
    modality: keys[cohort.campus === 'rmt' ? 'remote' : 'onCampus'],
    price: cohort.price.amount.toLocaleString(language, priceOptions),
    startDate: formatCohortDate(cohort.start_date, language),
    timezone: getTimezoneAbbreviation(timezone, language),
    track: keys[cohort.track],
    weekDayEndTime: cohortWeekDayTimes.end_time,
    weekDays,
    weekDayStartTime: cohortWeekDayTimes.start_time,
    weekendDays,
    weekendEndTime: cohortWeekendTimes.end_time,
    weekendStartTime: cohortWeekendTimes.start_time,
  };
};

export const getSalesforceCampus = (campus: Campus): string =>
  campus === 'rmt' ? `${campus.toUpperCase()}EU` : campus.toUpperCase();

export type PrettyCohort = ReturnType<typeof getPrettyCohort>;

// The keys of this object are used as the official list of course tracks:
//   1. A track has to be present here to be displayed in upcoming cohorts
//   2. Order matters! This order determines the order of upcoming cohorts tabs
//   3. Contact & syllabus form back-end validation only accept these tracks
/* eslint-disable sort-keys */
export const courseToBackground: Record<AvailableCourseTrack, BackgroundColor> =
  {
    wd: 'web',
    da: 'data',
    ux: 'ux',
    cy: 'cyber',
    ml: 'ml',
    ai: 'ai',
    dv: 'dv',
    mk: 'mk',
    ac: 'ac',
    de: 'de',
    ce: 'ce',
    pm: 'pm',
  };
/* eslint-enable sort-keys */

export const getBaseUrlFromPaginationUrl = (pathname: string) =>
  pathname.split('/page/')[0];

export const allowedRegionsForLanguage: Record<
  Exclude<DatoLocale, 'en_UK'>,
  Region[]
> = {
  de_DE: ['de'],
  en_GB: ['uk'],
  en_US: ['de', 'es', 'fr', 'nl', 'pt', 'us', 'ww'],
  es_ES: ['es'],
  fr_FR: ['fr'],
  nl_NL: ['nl'],
  pt_PT: ['pt'],
};

export const inIframe = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const getSegmentsFromPathname = pipe(split('/'), drop(1));

export const propOf = flip(prop);
